import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Link,
  SimpleGrid,
  Text,
  useToast,
  VStack,
  HStack,
  Tag,
  Progress,
} from "@chakra-ui/react";
import {
  ArrowForwardIcon,
  AtSignIcon,
  CheckCircleIcon,
  InfoOutlineIcon,
  UnlockIcon,
  ViewIcon,
  ViewOffIcon,
} from "@chakra-ui/icons";
import {
  FaCloudUploadAlt,
  FaCreditCard,
  FaMoneyBill,
  FaUser,
} from "react-icons/fa";
import AppModal from "@/components/ui/AppModal";
import GlassButton from "@/components/pressets/GlassButton";
import { useUser } from "@/context/UserContext";
import { useProfile } from "@/context/ProfileContext";
import { getAuthToken, setAuthToken } from "@/lib/authStorage";
import { quickRegister, login } from "@/api/authService";
import { addBillingData } from "@/api/personalDataService";
import { createBankRequest } from "@/api/bankRequestService";
import { generatePaymentUrl } from "@/lib/generatePaymentUrl";
import { getProduct, formatPrice } from "@/lib/products";
import { getActiveProfileId } from "@/lib/profileStorage";
import { listTaxProfiles } from "@/api/taxProfileService";

const initialRegisterState = {
  username: "",
  password: "",
  name: "",
  email: "",
  phoneNumber: "",
};

const initialBillingState = {
  identificacion: "",
  email: "",
  direccion: "",
  nombre: "",
  telefono: "",
};

const initialLoginState = {
  identifier: "",
  password: "",
};

const getErrorMessage = (error) => {
  switch (error) {
    case "email-taken":
      return "El correo electrónico ya está en uso.";
    case "username-taken":
      return "El nombre de usuario ya está en uso.";
    case "user-not-found":
      return "Usuario no encontrado.";
    case "incorrect-password":
      return "Contraseña incorrecta.";
    default:
      return "Ha ocurrido un error en el servidor.";
  }
};

/**
 * Unified checkout for plans and paid additional profiles.
 */
export default function CheckoutModal({
  isOpen,
  onClose,
  productId = "basic",
  product: productProp,
  paymentType: paymentTypeProp = null,
  profileId: profileIdProp,
  onSuccess,
  requireAuth = true,
  purchaseContext = "plan",
}) {
  const product = productProp || getProduct(productId) || getProduct("basic");
  const { user } = useUser();
  const { quota, refreshProfiles } = useProfile();
  const toast = useToast();

  const [step, setStep] = useState("auth");
  const [paymentType, setPaymentType] = useState(paymentTypeProp);
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [useAccountData, setUseAccountData] = useState(true);
  const [file, setFile] = useState(null);
  const [registerData, setRegisterData] = useState(initialRegisterState);
  const [billingData, setBillingData] = useState(initialBillingState);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [newUserData, setNewUserData] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginData, setLoginData] = useState(initialLoginState);
  const [loginError, setLoginError] = useState("");

  const steps = useMemo(() => {
    const list = [];
    if (requireAuth && !getAuthToken() && !user) list.push("auth");
    list.push("summary");
    if (!paymentTypeProp) list.push("method");
    list.push("billing");
    if ((paymentTypeProp || paymentType) === "bank") list.push("bank-upload");
    return list;
  }, [requireAuth, user, paymentTypeProp, paymentType]);

  const activeSteps = useMemo(() => {
    if (step === "success" || step === "error") return [...steps, step];
    return steps;
  }, [steps, step]);

  const stepIndex = Math.max(0, activeSteps.indexOf(step));
  const progress = ((stepIndex + 1) / Math.max(activeSteps.length, 1)) * 100;

  useEffect(() => {
    if (!isOpen) return;
    const token = getAuthToken();
    const start =
      requireAuth && !token && !user
        ? "auth"
        : paymentTypeProp
          ? "summary"
          : "summary";
    setStep(start);
    setPaymentType(paymentTypeProp);
  }, [isOpen, requireAuth, user, paymentTypeProp]);

  useEffect(() => {
    if (step !== "billing") return;
    const sourceBilling = quota?.billingData;
    if (useAccountData) {
      const source = user || newUserData || {};
      setBillingData((prev) => ({
        ...prev,
        nombre:
          sourceBilling?.nombre ||
          source.display_name ||
          source.name ||
          "",
        email: sourceBilling?.email || source.email || "",
        telefono:
          sourceBilling?.telefono || source.phoneNumber || "",
        direccion:
          sourceBilling?.direccion ||
          source.billingAddress ||
          "",
        identificacion:
          sourceBilling?.identificacion ||
          source.billingIdentification ||
          "",
      }));
    } else {
      setBillingData(initialBillingState);
    }
  }, [useAccountData, step, user, newUserData, quota]);

  const resolveProfileId = async () => {
    if (purchaseContext === "new-profile") return undefined;
    if (purchaseContext === "renewal") {
      if (profileIdProp) return profileIdProp;
      throw new Error("La renovación requiere un perfil existente.");
    }
    if (product.scope !== "profile") return undefined;
    if (profileIdProp) return profileIdProp;
    let profileId = getActiveProfileId();
    if (!profileId) {
      // First purchase: the account still has a single profile to bill.
      try {
        const list = await listTaxProfiles();
        const profiles = Array.isArray(list) ? list : [];
        profileId = profiles[0]?._id;
      } catch {
        /* the backend resolves the profile when we cannot */
      }
    }
    return profileId;
  };

  const handleClose = () => {
    setStep("auth");
    setRegisterData(initialRegisterState);
    setBillingData(initialBillingState);
    setUseAccountData(true);
    setFile(null);
    setShowLogin(false);
    setLoginData(initialLoginState);
    setLoginError("");
    setErrorMessage("");
    setPaymentType(paymentTypeProp);
    onClose?.();
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await quickRegister(registerData);
      if (res.status === "error") {
        toast({
          title: "Error",
          description: getErrorMessage(res.message),
          status: "error",
        });
      } else {
        setAuthToken(res.token);
        const userData = res.userData?.data || res.userData || {};
        setNewUserData({
          id: userData.id,
          username: userData.username,
          email: userData.email,
          name: userData.name,
          level: userData.level,
          billingIdentification: userData.billingIdentification,
          billingAddress: userData.billingAddress,
          phoneNumber: userData.phoneNumber,
        });
        setStep("summary");
      }
    } catch {
      toast({ title: "Error de servidor", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError("");
    try {
      const res = await login(loginData.identifier, loginData.password);
      if (res.status === "error") {
        setLoginError(getErrorMessage(res.message));
      } else {
        setAuthToken(res.token);
        const userData = res.userData || {};
        setNewUserData({
          id: userData.id,
          username: userData.username,
          email: userData.email,
          name: userData.name,
          level: userData.level,
          phoneNumber: userData.phoneNumber,
          billingIdentification: userData.billingIdentification,
          billingAddress: userData.billingAddress,
        });
        setShowLogin(false);
        setStep("summary");
      }
    } catch {
      setLoginError("Ha ocurrido un error en el servidor.");
    } finally {
      setLoading(false);
    }
  };

  const goAfterSummary = () => {
    if (!paymentTypeProp && !paymentType) {
      setStep("method");
      return;
    }
    setStep("billing");
  };

  const onSelectMethod = (method) => {
    setPaymentType(method);
    setStep("billing");
  };

  const onBilling = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addBillingData(billingData);
      const method = paymentTypeProp || paymentType;
      if (method === "card") {
        const uid = newUserData?.id || user?.id;
        const profileId = await resolveProfileId();
        window.location.href = await generatePaymentUrl(
          product.price,
          product.reference,
          uid,
          product.purchaseType,
          profileId,
          purchaseContext
        );
      } else {
        setLoading(false);
        setStep("bank-upload");
      }
    } catch {
      toast({
        title: "Error al guardar datos de facturación",
        status: "error",
      });
      setLoading(false);
    }
  };

  const onUploadReceipt = async () => {
    if (!file) {
      toast({ title: "Archivo requerido", status: "warning" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Archivo demasiado grande",
        description: "El tamaño máximo permitido es de 5MB.",
        status: "error",
      });
      return;
    }
    setLoading(true);
    try {
      const profileId = await resolveProfileId();
      const resolvedProductType =
        purchaseContext === "renewal"
          ? "renewal"
          : purchaseContext === "new-profile"
            ? "profile"
            : product.productType;
      await createBankRequest(file, product.purchaseType, {
        productType: resolvedProductType,
        profileId,
      });
      setStep("success");
      await refreshProfiles?.();
      onSuccess?.({ product, paymentType: "bank" });
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Error desconocido");
      setStep("error");
    } finally {
      setLoading(false);
    }
  };

  const ProductIcon = product.icon;

  const renderAuth = () =>
    showLogin ? (
      <VStack as="form" spacing={4} onSubmit={onLogin}>
        <FormControl isRequired>
          <FormLabel>Usuario o correo</FormLabel>
          <InputGroup>
            <InputLeftElement>
              <AtSignIcon color="gray.500" />
            </InputLeftElement>
            <Input
              name="identifier"
              value={loginData.identifier}
              onChange={(e) =>
                setLoginData((p) => ({ ...p, identifier: e.target.value }))
              }
            />
          </InputGroup>
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Contraseña</FormLabel>
          <InputGroup>
            <InputLeftElement>
              <UnlockIcon color="gray.500" />
            </InputLeftElement>
            <Input
              name="password"
              type={showLoginPassword ? "text" : "password"}
              value={loginData.password}
              onChange={(e) =>
                setLoginData((p) => ({ ...p, password: e.target.value }))
              }
            />
            <InputRightElement>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowLoginPassword((v) => !v)}
              >
                {showLoginPassword ? <ViewOffIcon /> : <ViewIcon />}
              </Button>
            </InputRightElement>
          </InputGroup>
        </FormControl>
        {loginError && (
          <Text color="red.500" fontSize="sm">
            {loginError}
          </Text>
        )}
        <GlassButton
          type="submit"
          w="full"
          colorScheme={product.color}
          isLoading={loading}
        >
          Continuar
        </GlassButton>
        <Text fontSize="sm">
          ¿No tienes cuenta?{" "}
          <Link color="blue.600" onClick={() => setShowLogin(false)}>
            Regístrate
          </Link>
        </Text>
      </VStack>
    ) : (
      <VStack as="form" spacing={4} onSubmit={onRegister}>
        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} w="full">
          <FormControl isRequired>
            <FormLabel>Nombre</FormLabel>
            <Input
              name="name"
              value={registerData.name}
              onChange={(e) =>
                setRegisterData((p) => ({ ...p, name: e.target.value }))
              }
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Usuario</FormLabel>
            <Input
              name="username"
              value={registerData.username}
              onChange={(e) =>
                setRegisterData((p) => ({ ...p, username: e.target.value }))
              }
            />
          </FormControl>
        </SimpleGrid>
        <FormControl isRequired>
          <FormLabel>Correo</FormLabel>
          <Input
            name="email"
            type="email"
            value={registerData.email}
            onChange={(e) =>
              setRegisterData((p) => ({ ...p, email: e.target.value }))
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Teléfono</FormLabel>
          <Input
            name="phoneNumber"
            value={registerData.phoneNumber}
            onChange={(e) =>
              setRegisterData((p) => ({ ...p, phoneNumber: e.target.value }))
            }
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Contraseña</FormLabel>
          <InputGroup>
            <Input
              name="password"
              type={showPassword ? "text" : "password"}
              value={registerData.password}
              onChange={(e) =>
                setRegisterData((p) => ({ ...p, password: e.target.value }))
              }
            />
            <InputRightElement>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <ViewOffIcon /> : <ViewIcon />}
              </Button>
            </InputRightElement>
          </InputGroup>
        </FormControl>
        <GlassButton
          type="submit"
          w="full"
          colorScheme={product.color}
          isLoading={loading}
        >
          Crear cuenta y continuar
        </GlassButton>
        <Text fontSize="sm">
          ¿Ya tienes cuenta?{" "}
          <Link color="blue.600" onClick={() => setShowLogin(true)}>
            Inicia sesión
          </Link>
        </Text>
      </VStack>
    );

  const renderSummary = () => (
    <VStack spacing={5} align="stretch">
      <Flex
        p={4}
        borderRadius="2xl"
        bg="blackAlpha.50"
        border="1px solid"
        borderColor={`${product.color}.300`}
        gap={4}
        align="center"
      >
        <Flex
          w="56px"
          h="56px"
          borderRadius="full"
          align="center"
          justify="center"
          bg={product.accent}
          color="white"
          flexShrink={0}
        >
          <Icon as={ProductIcon} boxSize={6} />
        </Flex>
        <Box>
          <Tag size="sm" colorScheme={product.color} mb={1}>
            {product.shortTitle}
          </Tag>
          <Heading size="sm">{product.title}</Heading>
          <Text fontSize="2xl" fontWeight="black" color={`${product.color}.600`}>
            {formatPrice(product.price)}
          </Text>
        </Box>
      </Flex>
      <VStack align="stretch" spacing={2}>
        {product.features.map((f) => (
          <HStack key={f} spacing={3}>
            <CheckCircleIcon color={`${product.color}.400`} />
            <Text fontSize="sm">{f}</Text>
          </HStack>
        ))}
      </VStack>
      <GlassButton
        colorScheme={product.color}
        w="full"
        rightIcon={<ArrowForwardIcon />}
        onClick={goAfterSummary}
      >
        Continuar al pago
      </GlassButton>
    </VStack>
  );

  const renderMethod = () => (
    <VStack spacing={4}>
      <Text color="gray.600" textAlign="center">
        Elige cómo quieres pagar {product.shortTitle.toLowerCase()}.
      </Text>
      <SimpleGrid columns={1} spacing={3} w="full">
        <Button
          h="auto"
          py={5}
          px={4}
          borderRadius="2xl"
          variant="outline"
          borderColor="blue.200"
          onClick={() => onSelectMethod("card")}
          leftIcon={<Icon as={FaCreditCard} boxSize={5} color="blue.600" />}
          justifyContent="flex-start"
        >
          <Box textAlign="left">
            <Text fontWeight="bold">Tarjeta</Text>
            <Text fontSize="sm" color="gray.500">
              Pago inmediato con PayPhone
            </Text>
          </Box>
        </Button>
        <Button
          h="auto"
          py={5}
          px={4}
          borderRadius="2xl"
          variant="outline"
          borderColor="teal.200"
          onClick={() => onSelectMethod("bank")}
          leftIcon={<Icon as={FaMoneyBill} boxSize={5} color="teal.600" />}
          justifyContent="flex-start"
        >
          <Box textAlign="left">
            <Text fontWeight="bold">Transferencia</Text>
            <Text fontSize="sm" color="gray.500">
              Sube tu comprobante para revisión
            </Text>
          </Box>
        </Button>
      </SimpleGrid>
    </VStack>
  );

  const renderBilling = () => (
    <VStack as="form" spacing={4} onSubmit={onBilling}>
      <Checkbox
        isChecked={useAccountData}
        onChange={(e) => setUseAccountData(e.target.checked)}
      >
        Usar mis datos de cuenta
      </Checkbox>
      <FormControl isRequired>
        <FormLabel>Nombre / razón social</FormLabel>
        <Input
          name="nombre"
          value={billingData.nombre}
          onChange={(e) =>
            setBillingData((p) => ({ ...p, nombre: e.target.value }))
          }
        />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Identificación / RUC</FormLabel>
        <Input
          name="identificacion"
          value={billingData.identificacion}
          onChange={(e) =>
            setBillingData((p) => ({ ...p, identificacion: e.target.value }))
          }
        />
      </FormControl>
      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3} w="full">
        <FormControl isRequired>
          <FormLabel>Email</FormLabel>
          <Input
            name="email"
            type="email"
            value={billingData.email}
            onChange={(e) =>
              setBillingData((p) => ({ ...p, email: e.target.value }))
            }
          />
        </FormControl>
        <FormControl>
          <FormLabel>Teléfono</FormLabel>
          <Input
            name="telefono"
            value={billingData.telefono}
            onChange={(e) =>
              setBillingData((p) => ({ ...p, telefono: e.target.value }))
            }
          />
        </FormControl>
      </SimpleGrid>
      <FormControl>
        <FormLabel>Dirección</FormLabel>
        <Input
          name="direccion"
          value={billingData.direccion}
          onChange={(e) =>
            setBillingData((p) => ({ ...p, direccion: e.target.value }))
          }
        />
      </FormControl>
      <GlassButton
        type="submit"
        w="full"
        colorScheme={product.color}
        isLoading={loading}
        leftIcon={
          <Icon
            as={
              (paymentTypeProp || paymentType) === "card"
                ? FaCreditCard
                : FaMoneyBill
            }
          />
        }
      >
        {(paymentTypeProp || paymentType) === "card"
          ? `Pagar ${formatPrice(product.price)}`
          : "Continuar a transferencia"}
      </GlassButton>
    </VStack>
  );

  const renderBankUpload = () => (
    <VStack spacing={5} align="stretch">
      <Box
        p={4}
        borderRadius="xl"
        bg="blackAlpha.50"
        border="1px solid"
        borderColor="teal.100"
      >
        <Text fontWeight="bold" mb={2}>
          Datos para transferencia
        </Text>
        <Text fontSize="sm">Banco: Produbanco</Text>
        <Text fontSize="sm">Cuenta de ahorros: 1200123456</Text>
        <Text fontSize="sm">RUC: 1793189477001</Text>
        <Text fontSize="sm" fontWeight="semibold" mt={2}>
          Monto: {formatPrice(product.price)} — {product.shortTitle}
        </Text>
        <HStack mt={2} spacing={1} color="gray.500">
          <InfoOutlineIcon boxSize={3} />
          <Text fontSize="xs">
            Usa tu nombre como referencia del depósito.
          </Text>
        </HStack>
      </Box>
      <FormControl>
        <FormLabel>Comprobante (imagen o PDF, máx. 5MB)</FormLabel>
        <Button
          as="label"
          leftIcon={<FaCloudUploadAlt />}
          variant="outline"
          w="full"
          h="56px"
          cursor="pointer"
        >
          {file ? file.name : "Seleccionar archivo"}
          <Input
            type="file"
            accept="image/*,.pdf"
            display="none"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </Button>
      </FormControl>
      <GlassButton
        colorScheme="teal"
        w="full"
        isLoading={loading}
        onClick={onUploadReceipt}
      >
        Enviar solicitud
      </GlassButton>
    </VStack>
  );

  const renderSuccess = () => (
    <VStack spacing={6} py={4} textAlign="center">
      <Icon as={CheckCircleIcon} boxSize={16} color="teal.400" />
      <Heading size="md">¡Solicitud enviada!</Heading>
      <Text color="gray.600">
        Revisaremos tu comprobante y{" "}
        {purchaseContext === "new-profile"
          ? "liberaremos tu nuevo perfil con el plan seleccionado"
          : purchaseContext === "renewal"
            ? "reactivaremos este perfil"
            : "liberaremos tu plan"}{" "}
        cuando esté aprobado.
      </Text>
      <GlassButton
        colorScheme="teal"
        w="full"
        onClick={() => {
          handleClose();
          if (
            purchaseContext === "new-profile" ||
            purchaseContext === "renewal"
          ) {
            window.location.href = "/perfiles";
          } else {
            window.location.href = "/";
          }
        }}
      >
        {purchaseContext === "new-profile" || purchaseContext === "renewal"
          ? "Ir a Mis perfiles"
          : "Ir al inicio"}
      </GlassButton>
    </VStack>
  );

  const renderError = () => (
    <VStack spacing={6} py={4} textAlign="center">
      <Icon as={FaUser} boxSize={12} color="red.400" />
      <Heading size="md" color="red.500">
        No se pudo completar
      </Heading>
      <Text color="gray.600">{errorMessage}</Text>
      <GlassButton colorScheme="teal" w="full" onClick={() => setStep("bank-upload")}>
        Volver a intentar
      </GlassButton>
    </VStack>
  );

  const titles = {
    auth: showLogin ? "Iniciar sesión" : "Crear cuenta",
    summary: "Resumen de compra",
    method: "Método de pago",
    billing: "Facturación",
    "bank-upload": "Confirmar transferencia",
    success: "¡Listo!",
    error: "Error",
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleClose}
      size={{ base: "full", md: "md" }}
      variant="solid"
      title={
        <Box w="full">
          <Progress
            value={progress}
            size="xs"
            colorScheme={product.color}
            borderRadius="full"
            mb={4}
          />
          {step === "auth" && (
            <Flex justify="center" mb={3}>
              <Image
                src="/assets/logo2025_dark.png"
                alt="ConTex"
                maxW="160px"
              />
            </Flex>
          )}
          <Text fontSize="xl" fontWeight="black">
            {titles[step] || "Checkout"}
          </Text>
        </Box>
      }
    >
      {step === "auth" && renderAuth()}
      {step === "summary" && renderSummary()}
      {step === "method" && renderMethod()}
      {step === "billing" && renderBilling()}
      {step === "bank-upload" && renderBankUpload()}
      {step === "success" && renderSuccess()}
      {step === "error" && renderError()}
    </AppModal>
  );
}
